import { apiClient } from '@/api/client'
import type { ApiResponse, ItemEntity, Warehouse } from '@/api/types'
import { endpoints } from '@/api/endpoints'
import type { InventoryAdjustmentRow } from '@/modules/inventory/adjustments/types'
import { inventoryMovementService } from '@/services/inventoryMovementService'

function unwrapData<T>(payload: unknown): T {
  if (payload && typeof payload === 'object') {
    const p = payload as Record<string, unknown>
    if ('success' in p && 'data' in p) return p.data as T
    if ('data' in p) return p.data as T
  }
  return payload as T
}

function mapItem(item: Record<string, unknown>): ItemEntity {
  return {
    itemId: Number(item?.itemId ?? item?.id ?? 0),
    itemNameEn: String(item?.itemNameEn ?? ''),
    itemNameAr: String(item?.itemNameAr ?? ''),
    itemCode: String(item?.itemCode ?? ''),
    reference: String(item?.reference ?? ''),
    itemCategoryId: Number(item?.itemCategoryId ?? 0),
    sku: String(item?.sku ?? ''),
    barcode: String(item?.barcode ?? ''),
    tags: String(item?.tags ?? ''),
    shelfId: String(item?.shelfId ?? ''),
    description: String(item?.description ?? ''),
    subCategoryId: Number(item?.subCategoryId ?? 0),
    uom: Number(item?.uom ?? 0),
    type: Number(item?.type ?? 0),
    costMethod: Number(item?.costMethod ?? 0),
    isTaxable: Boolean(item?.isTaxable ?? false),
    unitSellingPrice: Number(item?.unitSellingPrice ?? 0),
    unitCostPrice: Number(item?.unitCostPrice ?? 0),
    tax: Number(item?.tax ?? 0),
    sellingTax: Number(item?.sellingTax ?? 0),
    attachmentUrl: String(item?.attachment ?? item?.attachmentUrl ?? ''),
    createdDate: item?.createdDate as string | undefined,
    createdBy: item?.createdBy as number | undefined,
    modifiedDate: item?.modifiedDate as string | undefined,
    modifiedBy: item?.modifiedBy as number | undefined,
    branchId: Number(item?.branchId ?? 0),
  }
}

export type AdjustmentBatchRow = {
  id: number
  code: string
  reference: string
  availableQuantity: number
}

/** POST `/api/InventoryAdjustment` body (Swagger) */
export interface InventoryAdjustmentLineSavePayload {
  id: number
  adjustmentId: number
  itemId: number
  batchId: number
  warehouseId: number
  previousQuantity: number
  newQuantity: number
  quantityAdjusted: number
  isDeleted: string
}

export interface InventoryAdjustmentSavePayload {
  id: number
  code: string
  modeOfAdjustment: number
  modeOfAdjustmentValue: number
  adjustmentType: number
  reference: string
  adjustmentDate: string
  costAccount: number
  reason: string
  warehouseId: number
  branchId: number
  description: string
  attachmentFilePath: string
  itemList: InventoryAdjustmentLineSavePayload[]
}

/** GET GetAdjustmentById */
export interface InventoryAdjustmentMasterDto {
  id: number
  code: string
  modeOfAdjustment: number
  modeOfAdjustmentValue: number
  adjustmentType: number
  reference: string
  adjustmentDate: string
  costAccount: number
  reason: string
  warehouseId: number
  branchId: number
  description: string
  attachmentFilePath: string
}

/** GET GetAdjustmentLinesByAdjustmentId */
export interface InventoryAdjustmentLineDto {
  id: number
  adjustmentId: number
  itemId: number
  batchId: number
  warehouseId: number
  previousQuantity: number
  newQuantity: number
  quantityAdjusted: number
}

function mapAdjustmentMasterDto(x: Record<string, unknown>): InventoryAdjustmentMasterDto {
  const dateRaw = String(x.adjustmentDate ?? x.AdjustmentDate ?? '').trim()
  return {
    id: Number(x.id ?? x.Id ?? 0),
    code: String(x.code ?? x.Code ?? ''),
    modeOfAdjustment: Number(x.modeOfAdjustment ?? x.ModeOfAdjustment ?? 0),
    modeOfAdjustmentValue: Number(x.modeOfAdjustmentValue ?? x.ModeOfAdjustmentValue ?? 0),
    adjustmentType: Number(x.adjustmentType ?? x.AdjustmentType ?? 0),
    reference: String(x.reference ?? x.Reference ?? ''),
    adjustmentDate: dateRaw ? dateRaw.slice(0, 10) : '',
    costAccount: Number(x.costAccount ?? x.CostAccount ?? 0),
    reason: String(x.reason ?? x.Reason ?? ''),
    warehouseId: Number(x.warehouseId ?? x.WarehouseId ?? 0),
    branchId: Number(x.branchId ?? x.BranchId ?? 0),
    description: String(x.description ?? x.Description ?? ''),
    attachmentFilePath: String(x.attachmentFilePath ?? x.AttachmentFilePath ?? ''),
  }
}

function mapAdjustmentLineDto(x: Record<string, unknown>): InventoryAdjustmentLineDto {
  return {
    id: Number(x.id ?? x.Id ?? 0),
    adjustmentId: Number(x.adjustmentId ?? x.AdjustmentId ?? 0),
    itemId: Number(x.itemId ?? x.ItemId ?? 0),
    batchId: Number(x.batchId ?? x.BatchId ?? 0),
    warehouseId: Number(x.warehouseId ?? x.WarehouseId ?? 0),
    previousQuantity: Number(
      x.previousQuantity ?? x.PreviousQuantity ?? x.prevQuantity ?? x.PrevQuantity ?? 0,
    ),
    newQuantity: Number(x.newQuantity ?? x.NewQuantity ?? 0),
    quantityAdjusted: Number(x.quantityAdjusted ?? x.QuantityAdjusted ?? 0),
  }
}

function warehouseVmLabel(w: Warehouse): string {
  const c = w.code?.trim()
  const n = w.name?.trim()
  if (c && n) return `${c} — ${n}`
  return n || c || String(w.id)
}

function warehouseIdFromListRow(x: Record<string, unknown>): number {
  const n = Number(x.warehouseId ?? x.WarehouseId ?? 0)
  return Number.isFinite(n) && n > 0 ? n : 0
}

function mapInventoryAdjustmentListRow(x: Record<string, unknown>): InventoryAdjustmentRow {
  const idNum = Number(x.id ?? x.Id ?? 0)
  const whName = String(
    x.warehouseName ?? x.WarehouseName ?? x.whName ?? x.WhName ?? x.warehouse ?? x.Warehouse ?? '',
  ).trim()
  const whCode = String(x.warehouseCode ?? x.WarehouseCode ?? x.whCode ?? x.WhCode ?? '').trim()
  const whId = warehouseIdFromListRow(x)
  const warehouse =
    whName && whCode ? `${whCode} — ${whName}` : whName || whCode || (whId ? String(whId) : '')

  return {
    id: idNum > 0 ? String(idNum) : `tmp-${Math.random().toString(36).slice(2, 11)}`,
    code: String(x.code ?? x.Code ?? ''),
    reference: String(x.reference ?? x.Reference ?? ''),
    warehouse,
  }
}

function applyWarehouseLabels(
  itemsRaw: unknown[],
  rows: InventoryAdjustmentRow[],
  labelById: Map<number, string>,
): InventoryAdjustmentRow[] {
  return rows.map((r, idx) => {
    const x = itemsRaw[idx] as Record<string, unknown>
    const whId = warehouseIdFromListRow(x)
    const resolved = whId ? labelById.get(whId) : undefined
    if (!resolved) return r
    const w = r.warehouse.trim()
    if (w === String(whId) || w === '') {
      return { ...r, warehouse: resolved }
    }
    return r
  })
}

export class InventoryAdjustmentService {
  async getAdjustments(
    branchId: number,
    pageNumber: number,
    pageSize: number,
  ): Promise<{ items: InventoryAdjustmentRow[]; totalCount: number }> {
    const [response, warehouses] = await Promise.all([
      apiClient.get<ApiResponse<unknown> | unknown>(
        endpoints.inventoryAdjustment.getAdjustments(branchId, pageNumber, pageSize),
      ),
      inventoryMovementService.getWarehousesByBranchId(branchId).catch(() => [] as Warehouse[]),
    ])

    const payload = unwrapData<unknown>(response.data)

    let itemsRaw: unknown[] = []
    let totalCount = 0

    if (Array.isArray(payload)) {
      itemsRaw = payload
      totalCount = payload.length
    } else if (payload && typeof payload === 'object') {
      const p = payload as Record<string, unknown>
      const data = p.data ?? p.Data ?? p.items ?? p.Items
      if (Array.isArray(data)) itemsRaw = data
      totalCount = Number(
        p.totalCount ??
          p.TotalCount ??
          p.totalRecords ??
          p.TotalRecords ??
          p.total ??
          p.Total ??
          itemsRaw.length,
      )
      if (!Number.isFinite(totalCount)) totalCount = itemsRaw.length
    }

    const labelById = new Map<number, string>(warehouses.map((w) => [w.id, warehouseVmLabel(w)]))
    const baseRows = itemsRaw.map((row) => mapInventoryAdjustmentListRow(row as Record<string, unknown>))
    const items = applyWarehouseLabels(itemsRaw, baseRows, labelById)
    return { items, totalCount }
  }

  async getAdjustmentById(adjustmentId: number): Promise<InventoryAdjustmentMasterDto> {
    const response = await apiClient.get<ApiResponse<unknown> | unknown>(
      endpoints.inventoryAdjustment.getAdjustmentById(adjustmentId),
    )
    const payload = unwrapData<unknown>(response.data)
    let row: Record<string, unknown> | null = null
    if (Array.isArray(payload) && payload[0] && typeof payload[0] === 'object') {
      row = payload[0] as Record<string, unknown>
    } else if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
      const p = payload as Record<string, unknown>
      const inner = p.data ?? p.Data
      if (Array.isArray(inner) && inner[0] && typeof inner[0] === 'object') {
        row = inner[0] as Record<string, unknown>
      } else if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
        row = inner as Record<string, unknown>
      } else {
        row = p
      }
    }
    return mapAdjustmentMasterDto(row ?? {})
  }

  async getAdjustmentLinesByAdjustmentId(adjustmentId: number): Promise<InventoryAdjustmentLineDto[]> {
    const response = await apiClient.get<ApiResponse<unknown> | unknown>(
      endpoints.inventoryAdjustment.getAdjustmentLinesByAdjustmentId(adjustmentId),
    )
    const payload = unwrapData<unknown>(response.data)
    let arr: unknown[] = []
    if (Array.isArray(payload)) {
      arr = payload
    } else if (payload && typeof payload === 'object') {
      const p = payload as Record<string, unknown>
      const data = p.data ?? p.Data ?? p.items ?? p.Items
      if (Array.isArray(data)) arr = data
    }
    return arr.map((x) => mapAdjustmentLineDto(x as Record<string, unknown>))
  }

  async getItemsByWarehouse(whId: number): Promise<ItemEntity[]> {
    const response = await apiClient.get<ApiResponse<unknown> | unknown>(
      endpoints.inventoryAdjustment.getItemsByWarehouse(whId),
    )
    const payload = unwrapData<unknown>(response.data)
    const arr = Array.isArray(payload)
      ? payload
      : payload && typeof payload === 'object' && Array.isArray((payload as { data?: unknown }).data)
        ? (payload as { data: unknown[] }).data
        : []
    return arr.map((x) => mapItem(x as Record<string, unknown>))
  }

  async getBatchesByWarehouseAndItem(whId: number, itemId: number): Promise<AdjustmentBatchRow[]> {
    const response = await apiClient.get<ApiResponse<unknown> | unknown>(
      endpoints.inventoryAdjustment.getBatchesByWarehouseAndItem(whId, itemId),
    )
    const payload = unwrapData<unknown>(response.data)
    const arr = Array.isArray(payload)
      ? payload
      : payload && typeof payload === 'object' && Array.isArray((payload as { data?: unknown }).data)
        ? (payload as { data: unknown[] }).data
        : []
    return arr.map((x: Record<string, unknown>) => ({
      id: Number(x?.id ?? 0),
      code: String(x?.code ?? ''),
      reference: String(x?.reference ?? ''),
      availableQuantity: Number(x?.availableQuantity ?? x?.AvailableQuantity ?? 0),
    }))
  }

  async getItemDetails(whId: number, itemId: number, bId: number): Promise<{ availableQuantity: number }> {
    const response = await apiClient.get<ApiResponse<unknown> | unknown>(
      endpoints.inventoryAdjustment.getItemDetails(whId, itemId, bId),
    )
    const payload = unwrapData<Record<string, unknown>>(response.data)
    const raw =
      payload?.availableQuantity ??
      payload?.AvailableQuantity ??
      payload?.balanceQuantity ??
      payload?.BalanceQuantity ??
      payload?.quantity ??
      payload?.Quantity ??
      0
    const n = Number(raw)
    return { availableQuantity: Number.isFinite(n) ? n : 0 }
  }

  async save(payload: InventoryAdjustmentSavePayload): Promise<unknown> {
    // Swagger: Consumes application/json-patch+json (same pattern as LeavePolicy, permissions, etc.)
    const response = await apiClient.post<unknown>(endpoints.inventoryAdjustment.save, payload, {
      headers: {
        'Content-Type': 'application/json-patch+json',
        Accept: 'application/json',
      },
      skipHandledLocallyErrorUI: true,
    } as any)

    const root = response.data as Record<string, unknown> | null | undefined
    if (root && typeof root === 'object') {
      if (root.isError === true || root.IsError === true) {
        const text = String(root.message ?? root.Message ?? root.msg ?? root.Msg ?? 'Save failed')
        throw new Error(text)
      }
      const inner = root.data ?? root.Data
      if (inner && typeof inner === 'object') {
        const o = inner as Record<string, unknown>
        if (o.isError === true || o.IsError === true) {
          const text = String(o.message ?? o.Message ?? root.message ?? root.Message ?? 'Save failed')
          throw new Error(text)
        }
      }
    }

    return response.data
  }
}

export const inventoryAdjustmentService = new InventoryAdjustmentService()
